import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/RequestDetail.js
//   - src/pages/ClientDetail.js (client email is editable from the client
//     detail page; affects future requests, not the link in past ones)
//   - src/lib/supabase.js (cancelRequest, reopenRequest, deleteRequest,
//     addTransactionsToRequest, updateTransactionDescriptions; NO
//     changeRecipient / changeEmail / uploadOnBehalf functions)
// Removed:
//   - "Send reminder" button as a separate manual-reminder feature -- doesn't
//     exist. The "Send email" button re-sends the *request* email using the
//     request template, not a reminder template.
//   - "Change recipient" feature -- does not exist. To change the email on
//     a sent request, the bookkeeper would need to edit the client's email
//     (affecting future requests) and manually share the link via another
//     channel (copy link from the request page).
//   - "Upload on client's behalf" button -- does not exist. The bookkeeper
//     can't upload directly to a transaction from the request page.
//   - Inline single-row description editing -- not supported. Editing is
//     bulk only (select rows, click Edit descriptions).
//   - "Edit transactions" as a single button -- there are two separate
//     features: "+ Add transactions" (manual or CSV) and "✏️ Edit
//     descriptions" (bulk, only when rows are selected).
//   - Cancellation described as terminal -- it's reversible (Reopen button
//     appears on cancelled requests).
// Pass 2 added:
//   - Two-step cancel button (first click primes, second confirms)
//   - Reopen button on cancelled requests
//   - Single-row delete via the 🗑 icon on each row
//   - Role-based delete permission (assistants cannot delete)

export default {
  title:   'Resending or editing a request',
  summary: 'Fixing typos, adding or removing transactions, resending the email, cancelling, and deleting.',
  body: (
    <>
      <P>
        Once a request is sent, it isn't locked. The client's link stays
        valid even if you change the transactions, and you can re-send
        the email, cancel the request, or delete it entirely from the
        request page.
      </P>

      <H>Re-sending the request email</H>
      <P>
        If the client says they didn't receive the email, or asks for
        the link again, use the <Code>📧 Send email</Code> button at
        the top of the request page. It sends the same request email
        using your standard request template, to the email address on
        the client record.
      </P>
      <P>
        Receipt Chaser also sends one automatic reminder if the client
        hasn't completed the request after the scheduled number of
        days. The <Code>📧 Send email</Code> button does not use the
        reminder template; it re-sends the original request email.
      </P>

      <Callout variant="tip" title="When to chase manually">
        Even with automatic reminders, sometimes a personal message
        through your usual channel (text, WhatsApp, a phone call)
        gets a faster response. Copy the link with the{' '}
        <Code>🔗 Copy client link</Code> button and send it however
        the client actually reads things.
      </Callout>

      <H>Adding transactions to an existing request</H>
      <P>
        If you missed some transactions, you can add them after the
        fact. Click <Code>+ Add transactions</Code> above the
        transaction table. A dialog opens with two tabs:
      </P>
      <UL>
        <Li>
          <strong>Type manually.</strong> A small editable grid where
          you can fill in date, description, amount, and payee for
          each new row. A toggle on each row marks it as expenditure
          (default) or income.
        </Li>
        <Li>
          <strong>Upload CSV.</strong> Drop in a CSV of the additional
          transactions. Auto-detect, custom mapping, and the same
          rules as a brand new request all apply. See{' '}
          <Link to="/support/importing-transactions">Importing transactions</Link>.
        </Li>
      </UL>
      <P>
        New transactions appear as pending in the client's view next
        time they open the link. They won't get an automatic email
        about the addition, so it's worth a manual nudge to let them
        know there's more to do.
      </P>

      <Screenshot
        src="/support-screenshots/08 - add-transactions.jpg"
        alt="An open request page with the + Add transactions button highlighted in an orange outline at the top right of the transaction table, above the Showing dropdown."
        caption="The + Add transactions button at the top of the request's transaction table."
      />

      <H>Editing descriptions</H>
      <P>
        If your client says "I don't recognise SQ *MERCHANT 1234", you
        can rewrite the description. Tick the row (or rows) you want
        to edit, then click <Code>✏️ Edit descriptions</Code> in the
        bulk action bar at the top of the table. A modal opens
        letting you edit each selected description individually
        before saving them all at once.
      </P>
      <P>
        Editing descriptions doesn't reset any uploaded receipts. The
        attachments stay with their row, just with the new description.
        The next time the client opens the link, the new descriptions
        appear.
      </P>

      <Screenshot
        src="/support-screenshots/08 - edit descriptions.png"
        alt="The Edit descriptions modal showing two transaction rows being edited. Each row shows the date and amount on the right, an input field with the new description, and a 'was: ...' line below showing the original. A Cancel button and a green Save changes button sit at the bottom."
        caption="The Edit descriptions modal. The original description is shown below each input as a 'was: ...' line so you can see what you're changing."
      />

      <H>Removing transactions</H>
      <P>
        Two routes:
      </P>
      <UL>
        <Li>
          <strong>Single row.</strong> Click the 🗑 icon at the end
          of the row.
        </Li>
        <Li>
          <strong>Multiple rows.</strong> Tick the rows you want to
          remove, then click <Code>🗑 Delete selected</Code> in the
          bulk action bar.
        </Li>
      </UL>
      <P>
        Removing a transaction deletes the row and any file already
        uploaded against it. The action is permanent. Confirm only
        when you're sure.
      </P>

      <H>Changing who receives the request</H>
      <P>
        Receipt Chaser doesn't have a per-request "change recipient"
        option. Pick the route that matches your situation:
      </P>
      <P>
        <strong>The contact has changed permanently</strong> (they've
        left the business, or you had the wrong email all along).
        Open the client's detail page and click{' '}
        <Code>Edit details</Code> to update the email address. This
        change applies to all future requests for this client. For a
        request that's already been sent, click{' '}
        <Code>📧 Send email</Code> at the top of the request page to
        re-send the original email to the new address (Receipt Chaser
        uses whatever email is currently on the client record).
      </P>
      <P>
        <strong>One-off: someone else needs to see this specific
        request</strong> (a new accountant copied in, or a colleague
        helping with this one batch of receipts). Click{' '}
        <Code>🔗 Copy client link</Code> at the top of the request
        page and email the link manually to whoever needs it. The
        link stays valid regardless of who you sent it to. The
        client record stays unchanged.
      </P>

      <H>Cancelling a request</H>
      <P>
        If a request was sent in error (wrong client, wrong
        transactions, no longer needed), click{' '}
        <Code>⊘ Cancel request</Code> at the top of the request page.
        The button is two-step: the first click primes it (text
        changes to "Confirm cancel"), the second click commits. If
        you don't click again within 5 seconds, the prime resets.
      </P>
      <P>
        Cancelling:
      </P>
      <UL>
        <Li>Stops the client from uploading anything more (their link page shows a "no longer active" message)</Li>
        <Li>Keeps any files already uploaded</Li>
        <Li>Stops scheduled reminders for this request</Li>
        <Li>Moves the request to the <Code>Cancelled</Code> tab on the client's detail page</Li>
      </UL>

      <Callout variant="info" title="Cancellation is reversible">
        On a cancelled request you'll see a <Code>↻ Reopen request</Code>{' '}
        button at the top. Clicking it puts the request back in
        Outstanding and re-enables the client's upload link. Reminders
        don't automatically reschedule, so check the reminder card if
        you want one fired.
      </Callout>

      <H>Deleting a request</H>
      <P>
        Click <Code>🗑 Delete request</Code> at the top of the request
        page. Deleting removes everything: the transactions, the
        uploaded files, the lost declarations, and the request record
        itself. It cannot be undone.
      </P>
      <P>
        Deletion is only available to practice users with Owner or
        Admin role. Assistants cannot delete requests; they can
        cancel them instead.
      </P>

      <Callout variant="warning" title="Cancel vs Delete">
        Cancel is the safer choice in almost every case. It stops the
        client from uploading anything new but preserves everything
        for your records, and you can reopen it later. Delete is for
        cases where the request was genuinely a mistake and you want
        no trace of it.
      </Callout>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/reading-request-progress">Reading a request's progress at a glance</Link>
        </Li>
        <Li>
          <Link to="/support/client-didnt-get-email">My client says they didn't get the email</Link>
        </Li>
      </UL>
    </>
  ),
}
